import React from 'react';
import ProgressReportTemplateUrl from '../../../assets/handouts/PPReport.svg';
import {
  getCertificateTextFontSize,
  loadCertificateTemplate,
} from '../../admin-page/pages/facilitator/pages/hybrid/certificateTemplate';
import { useBadgeCompletionQuery } from './useBadgeCompletionQuery';

const ParentProgressReportDownloadLink = React.lazy(
  () => import('./ParentProgressReportDownloadLink'),
);

const COMPLETION_DATE_TARGETS = [
  { moduleNumber: 1, x: 473, y: 432 },
  { moduleNumber: 2, x: 473, y: 478 },
  { moduleNumber: 3, x: 473, y: 528 },
  { moduleNumber: 4, x: 473, y: 574 },
  { moduleNumber: 5, x: 473, y: 622 },
  { moduleNumber: 6, x: 473, y: 672 },
];

const PROGRESS_REPORT_TEMPLATE_OPTIONS = {
  includeDefaultTextTargets: false,
  excludedPathIndexes: [1, 2, 3, 4, 5, 6, 352],
  textTargets: {
    name: {
      x: 311,
      y: 276,
      dominantBaseline: 'auto',
      textAnchor: 'middle',
      fontSize: 24,
      fill: '#272E68',
      pdfFontFamily: 'Helvetica-Bold',
    },
    ...Object.fromEntries(
      COMPLETION_DATE_TARGETS.map(({ moduleNumber, x, y }) => [
        `completion-status-${moduleNumber}`,
        {
          x,
          y,
          dominantBaseline: 'auto',
          textAnchor: 'start',
          fontSize: 16,
          fill: '#000000',
          pdfFontFamily: 'Helvetica',
        },
      ]),
    ),
  },
};

const formatCompletionDate = (dateCompleted) => {
  if (!dateCompleted) {
    return '';
  }

  const dateParts = String(dateCompleted).match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (dateParts) {
    const [, year, month, day] = dateParts;
    return `${month}/${day}/${year}`;
  }

  const completionDate = new Date(dateCompleted);

  return Number.isNaN(completionDate.getTime())
    ? ''
    : new Intl.DateTimeFormat('en-US').format(completionDate);
};

const getProgressReportFileName = (name) => {
  const safeName = String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return `${safeName || 'parent'}-ezparent-progress-report.pdf`;
};

const ParentProgressReport = ({
  completions,
  parentName = localStorage.getItem('name')?.trim() || 'Parent',
  className = 'btn-1',
}) => {
  const hasProvidedCompletions = Array.isArray(completions);
  const completionQuery = useBadgeCompletionQuery({
    enabled: !hasProvidedCompletions,
  });
  const completionData = hasProvidedCompletions
    ? completions
    : completionQuery.data;
  const [template, setTemplate] = React.useState(null);
  const [templateError, setTemplateError] = React.useState('');

  const completionDates = React.useMemo(
    () =>
      Object.fromEntries(
        (completionData || []).map((completion) => [
          Number(completion.moduleNumber),
          formatCompletionDate(completion.dateCompleted),
        ]),
      ),
    [completionData],
  );
  const parentNameFontSize = getCertificateTextFontSize(parentName, {
    maximumFontSize: 24,
    minimumFontSize: 12,
    maxWidth: 300,
  });

  React.useEffect(() => {
    let isCurrent = true;

    loadCertificateTemplate(
      ProgressReportTemplateUrl,
      PROGRESS_REPORT_TEMPLATE_OPTIONS,
    )
      .then((loadedTemplate) => {
        if (isCurrent) {
          setTemplate(loadedTemplate);
        }
      })
      .catch((error) => {
        if (isCurrent) {
          setTemplateError(error.message);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  if (
    (!hasProvidedCompletions && completionQuery.isPending) ||
    (!template && !templateError)
  ) {
    return (
      <button type='button' className={className} disabled>
        Preparing Progress Report...
      </button>
    );
  }

  if (
    (!hasProvidedCompletions && completionQuery.isError) ||
    templateError
  ) {
    return (
      <button
        type='button'
        className={`${className} not-complete`.trim()}
        disabled
        title={templateError || undefined}
      >
        Progress Report Unavailable
      </button>
    );
  }

  return (
    <React.Suspense
      fallback={
        <button type='button' className={className} disabled>
          Preparing Progress Report...
        </button>
      }
    >
      <ParentProgressReportDownloadLink
        template={template}
        parentName={parentName}
        parentNameFontSize={parentNameFontSize}
        completionDates={completionDates}
        fileName={getProgressReportFileName(parentName)}
        className={className}
      >
        {({ loading, error }) =>
          error
            ? 'Progress Report Unavailable'
            : loading
              ? 'Preparing Progress Report...'
              : 'Download Progress Report'
        }
      </ParentProgressReportDownloadLink>
    </React.Suspense>
  );
};

export default ParentProgressReport;
